import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, Skeleton, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceReviewService, { MarketplaceReview, ReviewSummary } from '../../../services/marketplaceReviewService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import StarRating from './StarRating';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString('de-DE');

const ReviewItem: React.FC<{
    review: MarketplaceReview;
    canReply: boolean;
    onReplied: (review: MarketplaceReview) => void;
}> = ({ review, canReply, onReplied }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(review.sellerReply ?? '');
    const [saving, setSaving] = useState(false);

    const save = async (value: string) => {
        setSaving(true);
        try {
            const updated = await marketplaceReviewService.reply(review.id, value);
            notify(translate('marketplaceReviews.replySaved'), { type: 'success' });
            setEditing(false);
            setText(updated.sellerReply ?? '');
            onReplied(updated);
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceReviews.replyError')), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box component="li" data-testid={`review-${review.id}`} sx={{ listStyle: 'none', py: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <StarRating value={review.rating} size={18} />
                {review.title && <Typography variant="subtitle2" component="h3">{review.title}</Typography>}
            </Box>
            <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>
                {review.mine ? translate('marketplaceReviews.mine') : review.reviewerName ?? translate('marketplaceReviews.anonymous')}
                {' · '}
                {formatDate(review.createdAt)}
            </Typography>
            {review.body && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{review.body}</Typography>}

            {review.sellerReply && !editing && (
                <Box sx={{ mt: 1.5, ml: { xs: 1, sm: 3 }, pl: 2, borderLeft: '3px solid var(--oa-divider)' }} data-testid={`reply-${review.id}`}>
                    <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>
                        {translate('marketplaceReviews.sellerReply')}
                        {review.sellerReplyAt ? ` · ${formatDate(review.sellerReplyAt)}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{review.sellerReply}</Typography>
                </Box>
            )}

            {canReply && !editing && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => setEditing(true)}>
                    {translate(review.sellerReply ? 'marketplaceReviews.editReply' : 'marketplaceReviews.reply')}
                </Button>
            )}
            {canReply && editing && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                        label={translate('marketplaceReviews.replyLabel')}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 1000 } }}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" size="small" disabled={saving} onClick={() => save(text)}>
                            {translate('marketplaceReviews.replySave')}
                        </Button>
                        {review.sellerReply && (
                            <Button size="small" disabled={saving} onClick={() => save('')}>
                                {translate('marketplaceReviews.replyRemove')}
                            </Button>
                        )}
                        <Button size="small" disabled={saving} onClick={() => setEditing(false)}>
                            {translate('marketplaceReviews.cancel')}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

/**
 * Bewertungen auf der Listing-Detailseite (T9): Durchschnitt, Anzahl, Liste. Ist das
 * Angebot das eigene (`canReply`), kann der Anbieter je Bewertung antworten. Lädt
 * selbst; ein Fehler blendet nur diesen Abschnitt aus, nie die Detailseite.
 */
const ListingReviews: React.FC<{ listingId: number; canReply?: boolean }> = ({ listingId, canReply = false }) => {
    const translate = useTranslate();
    const [summary, setSummary] = useState<ReviewSummary | null>(null);
    const [reviews, setReviews] = useState<MarketplaceReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const data = await marketplaceReviewService.getListingReviews(listingId);
            setSummary(data.summary);
            setReviews(data.reviews);
            setError(null);
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceReviews.loadError')));
        } finally {
            setLoading(false);
        }
    }, [listingId, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    if (loading) return <Skeleton variant="rectangular" height={96} />;
    if (error) return <Alert severity="warning">{error}</Alert>;

    return (
        <section className="mk-card mk-dsec" aria-labelledby="reviews-heading" data-testid="listing-reviews">
            <h2 id="reviews-heading">{translate('marketplaceReviews.title')}</h2>
            {summary && summary.count > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <StarRating value={Number(summary.average ?? 0)} size={22} testId="reviews-average-stars" />
                    <Typography variant="body2" data-testid="reviews-average">
                        {translate('marketplaceReviews.average', { value: Number(summary.average ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}
                        {' · '}
                        {translate('marketplaceReviews.count', { count: summary.count })}
                    </Typography>
                </Box>
            ) : (
                <div className="mk-norev">
                    <MaterialSymbol name="rate_review" />
                    <div>{translate('marketplaceReviews.noReviews')}</div>
                </div>
            )}
            {reviews.length > 0 && (
                <>
                    <Divider sx={{ mt: 2 }} />
                    <Box component="ul" sx={{ m: 0, p: 0 }}>
                        {reviews.map((review, i) => (
                            <React.Fragment key={review.id}>
                                {i > 0 && <Divider component="li" />}
                                <ReviewItem
                                    review={review}
                                    canReply={canReply}
                                    onReplied={(updated) => setReviews((list) => list.map((r) => (r.id === updated.id ? updated : r)))}
                                />
                            </React.Fragment>
                        ))}
                    </Box>
                </>
            )}
        </section>
    );
};

export default ListingReviews;
